/**
 * vocab.ts — Display labels and canonical ordering for Period / Movement / Genre.
 *
 * The TYPE-level closed unions live in schema.ts (so the compiler catches
 * typos in author JSONs). This file ships the human-readable labels (EN + IT)
 * and the order in which filter UIs render values.
 *
 * Adding a new vocab value: add it to BOTH the union in schema.ts AND the
 * entry here. The render layer reads from here exclusively.
 */

import type { Period, Movement, Genre, ThemeId } from './schema';

export interface VocabEntry<T extends string> {
  id: T;
  label_en: string;
  label_it: string;
  order: number;
}

// ══════ PERIODS (chronological, UK + US interleaved) ══════

export const PERIOD_VOCAB: ReadonlyArray<VocabEntry<Period>> = [
  { id: 'old-english-medieval',  label_en: 'Old English & Medieval',   label_it: 'Antico inglese e medievale', order: 1 },
  { id: 'renaissance',           label_en: 'Renaissance',              label_it: 'Rinascimento',                order: 2 },
  { id: 'restoration-18c',       label_en: 'Restoration & 18th c.',    label_it: 'Restaurazione e Settecento',  order: 3 },
  { id: 'colonial',              label_en: 'Colonial (US)',            label_it: 'Coloniale (USA)',             order: 4 },
  { id: 'romantic',              label_en: 'Romantic',                 label_it: 'Romantico',                   order: 5 },
  { id: 'american-romanticism',  label_en: 'American Romanticism',     label_it: 'Romanticismo americano',      order: 6 },
  { id: 'victorian',             label_en: 'Victorian',                label_it: 'Vittoriano',                  order: 7 },
  { id: 'american-realism',      label_en: 'American Realism',         label_it: 'Realismo americano',          order: 8 },
  { id: 'modernist',             label_en: 'Modernist',                label_it: 'Modernista',                  order: 9 },
  { id: 'contemporary',          label_en: 'Contemporary',             label_it: 'Contemporaneo',               order: 10 },
];

// ══════ MOVEMENTS ══════

export const MOVEMENT_VOCAB: ReadonlyArray<VocabEntry<Movement>> = [
  { id: 'humanism',          label_en: 'Humanism',           label_it: 'Umanesimo',           order: 1 },
  { id: 'metaphysical',      label_en: 'Metaphysical',       label_it: 'Metafisici',          order: 2 },
  { id: 'neoclassicism',     label_en: 'Neoclassicism',      label_it: 'Neoclassicismo',      order: 3 },
  { id: 'romanticism',       label_en: 'Romanticism',        label_it: 'Romanticismo',        order: 4 },
  { id: 'transcendentalism', label_en: 'Transcendentalism',  label_it: 'Trascendentalismo',   order: 5 },
  { id: 'realism',           label_en: 'Realism',            label_it: 'Realismo',            order: 6 },
  { id: 'naturalism',        label_en: 'Naturalism',         label_it: 'Naturalismo',         order: 7 },
  { id: 'aestheticism',      label_en: 'Aestheticism',       label_it: 'Estetismo',           order: 8 },
  { id: 'modernism',         label_en: 'Modernism',          label_it: 'Modernismo',          order: 9 },
  { id: 'harlem-renaissance',label_en: 'Harlem Renaissance', label_it: 'Harlem Renaissance',  order: 10 },
  { id: 'beat',              label_en: 'Beat Generation',    label_it: 'Beat Generation',     order: 11 },
  { id: 'confessional',      label_en: 'Confessional Poetry',label_it: 'Poesia confessionale',order: 12 },
  { id: 'southern-gothic',   label_en: 'Southern Gothic',    label_it: 'Southern Gothic',     order: 13 },
  { id: 'postmodernism',     label_en: 'Postmodernism',      label_it: 'Postmodernismo',      order: 14 },
];

// ══════ GENRES / FORMS ══════

export const GENRE_VOCAB: ReadonlyArray<VocabEntry<Genre>> = [
  // Drama
  { id: 'tragedy',                 label_en: 'Tragedy',                 label_it: 'Tragedia',                order: 1 },
  { id: 'comedy',                  label_en: 'Comedy',                  label_it: 'Commedia',                order: 2 },
  { id: 'history-play',            label_en: 'History Play',            label_it: 'Dramma storico',          order: 3 },
  // Poetry
  { id: 'sonnet',                  label_en: 'Sonnet',                  label_it: 'Sonetto',                 order: 4 },
  { id: 'ode',                     label_en: 'Ode',                     label_it: 'Ode',                     order: 5 },
  { id: 'lyric-poetry',            label_en: 'Lyric Poetry',            label_it: 'Poesia lirica',           order: 6 },
  { id: 'epic',                    label_en: 'Epic',                    label_it: 'Epica',                   order: 7 },
  { id: 'dramatic-monologue',      label_en: 'Dramatic Monologue',      label_it: 'Monologo drammatico',     order: 8 },
  { id: 'blank-verse',             label_en: 'Blank Verse',             label_it: 'Verso sciolto',           order: 9 },
  { id: 'free-verse',              label_en: 'Free Verse',              label_it: 'Verso libero',            order: 10 },
  // Prose fiction
  { id: 'novel',                   label_en: 'Novel',                   label_it: 'Romanzo',                 order: 11 },
  { id: 'gothic-novel',            label_en: 'Gothic Novel',            label_it: 'Romanzo gotico',          order: 12 },
  { id: 'bildungsroman',           label_en: 'Bildungsroman',           label_it: 'Bildungsroman',           order: 13 },
  { id: 'short-story',             label_en: 'Short Story',             label_it: 'Racconto',                order: 14 },
  { id: 'stream-of-consciousness', label_en: 'Stream of Consciousness', label_it: 'Flusso di coscienza',     order: 15 },
  // Non-fiction
  { id: 'satire',                  label_en: 'Satire',                  label_it: 'Satira',                  order: 16 },
  { id: 'essay',                   label_en: 'Essay',                   label_it: 'Saggio',                  order: 17 },
];

// ══════ Lookup helpers ══════

const PERIOD_BY_ID = new Map(PERIOD_VOCAB.map(e => [e.id, e]));
const MOVEMENT_BY_ID = new Map(MOVEMENT_VOCAB.map(e => [e.id, e]));
const GENRE_BY_ID = new Map(GENRE_VOCAB.map(e => [e.id, e]));

export type UiLocale = 'en' | 'it';

export function periodLabel(id: Period, locale: UiLocale): string {
  const e = PERIOD_BY_ID.get(id);
  return e ? (locale === 'en' ? e.label_en : e.label_it) : id;
}

export function movementLabel(id: Movement, locale: UiLocale): string {
  const e = MOVEMENT_BY_ID.get(id);
  return e ? (locale === 'en' ? e.label_en : e.label_it) : id;
}

export function genreLabel(id: Genre, locale: UiLocale): string {
  const e = GENRE_BY_ID.get(id);
  return e ? (locale === 'en' ? e.label_en : e.label_it) : id;
}

/** Theme id is Movement | Genre — resolves in either vocab. */
export function themeLabel(id: ThemeId, locale: UiLocale): string {
  const mv = MOVEMENT_BY_ID.get(id as Movement);
  if (mv) return locale === 'en' ? mv.label_en : mv.label_it;
  const gn = GENRE_BY_ID.get(id as Genre);
  if (gn) return locale === 'en' ? gn.label_en : gn.label_it;
  return id;
}

/** "movement" | "genre" | "unknown" — useful for the ThemeMapView lens. */
export function themeKind(id: ThemeId): 'movement' | 'genre' | 'unknown' {
  if (MOVEMENT_BY_ID.has(id as Movement)) return 'movement';
  if (GENRE_BY_ID.has(id as Genre)) return 'genre';
  return 'unknown';
}

/**
 * Etichetta localizzata del TIPO di un nodo mappa (opera / movimento / genere /
 * tema). Usata come caption nei nodi della mappa autore e della mappa temi:
 * prima erano stringhe inglesi hardcoded ("Work ▶", "GENRE") anche con UI in
 * italiano. 'unknown' ricade su "tema".
 */
const NODE_KIND_LABELS: Record<string, { it: string; en: string }> = {
  work:     { it: 'Opera',     en: 'Work' },
  movement: { it: 'Movimento', en: 'Movement' },
  genre:    { it: 'Genere',    en: 'Genre' },
  theme:    { it: 'Tema',      en: 'Theme' },
  unknown:  { it: 'Tema',      en: 'Theme' },
};

export function nodeKindLabel(kind: string, locale: UiLocale): string {
  const e = NODE_KIND_LABELS[kind];
  return e ? (locale === 'en' ? e.en : e.it) : kind;
}
